import React, { useCallback, useMemo } from 'react';

import Rect from '@lyearn/core/components/Rect';
import useToggle from '@lyearn/core/hooks/useToggle';
import { TFunction, Trans, useTranslation } from '@lyearn/i18n';
import { CloudUploadOutline, Plus } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { LinkButton, Stack, Typography } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import ErrorState from '@/components/Error';
import SortAndFilter from '@/components/FilterManager/SortAndFilter';
import Loader from '@/components/Loader';
import { PreserveModalForm, usePreserveModalForm } from '@/components/PreserveModalForm';
import Table from '@/components/Table';
import { commonTableStyles } from '@/components/Table/styles';
import AdminEmptyState from '@/modules/corporate/modules/admin/components/EmptyState';
import { JobMetaType, JobPurposeEnum } from '@/types/schema';

import BulkImport from '../../modules/bulk-import';
import BulkImportDialog from '../../modules/bulk-import/components/BulkImportDialog';
import { JobStateFragment } from '../../modules/bulk-import/graphql/fragments/generated/JobState';
import useImportProps from '../../modules/bulk-import/hooks/useImportProps';
import useLearnerAndGroupDialog from './hooks/useLearnerAndGroupDialog';
import CreateGroupModal from './CreateGroupModal';
import { tableStyles } from './styles';
import { Tabs } from './types';
import { LearnerAndGroupDialogProps } from './types';
import { getTableColumns, TRANSLATIONS } from './utils';

const TEMPLATE_FILE_NAME = 'import assignment learners sample';

export const getUpdateDataDialogDescription = (_: TFunction, updateData: JobMetaType) => (
  <Trans
    i18nKey={'Admin:LearnerAndGroups.UpdateDataDialogDescription'}
    values={{ count: updateData.assignmentLearnerImportMetadata?.userIds.length ?? 0 }}
  />
);

function LearnerAndGroupDialog(props: LearnerAndGroupDialogProps) {
  const { t } = useTranslation(['Admin', 'Assignment']);

  const {
    onClose,
    onAddLearnerAndGroup,
    title,
    description,
    primaryButtonText,
    secondaryButtonText,
    searchPlaceholder,
    selectedUserGroupIds,
    selectedLearnerIds,
    disabled,
    canImport = true,
    hideSecondaryAction,
    hideGroupsTab,
    hideUsersTab,
    isInGroupCheckId,
    importedUserGroupIds,
    removedUserIds,
  } = props;

  const {
    data: {
      isFetching,
      data,
      fetchMore,
      selectedCount,
      error,
      asyncList: {
        methods: { onChange, onSelectAllChange, isItemSelected },
        state: { isSelectAll, isPartiallySelected },
      },
      sortAndFilterItems,
      filterManager: {
        state: { sort, filter, search },
        methods: { setSort, setFilter, setSearch, clearSearch, clearFilters },
      },
    },
    onSubmit,
    tabs,
    inViewTab,
    isSubmitting,
    isImportInProgress,
    setImportJobStatus,
  } = useLearnerAndGroupDialog({
    onAddLearnerAndGroup,
    selectedUserGroupIds,
    selectedLearnerIds,
    importedUserGroupIds,
    removedUserIds,
    disabled,
    hideGroupsTab,
    hideUsersTab,
    isInGroupCheckId,
  });

  const [showLearnerImportDialog, { on, off }] = useToggle(false);
  const [openGroupCreationModal, { toggle: toggleGroupCreationModal }] = useToggle(false);

  const { fileInputRef, showFileSelector, uppy, currentImportJob, refetchJobs, onClear } =
    useImportProps({
      purpose: JobPurposeEnum.AssignmentLearnerImport,
    });

  const onCloseImport = useCallback(() => {
    onClear();
    setImportJobStatus(undefined);
  }, [onClear, setImportJobStatus]);

  const onJobComplete = useCallback(
    (job: JobStateFragment) => {
      const importedUserIds = job.meta?.assignmentLearnerImportMetadata?.userIds;
      const userGroupId = job.meta?.assignmentLearnerImportMetadata?.userGroupId;
      if (userGroupId) {
        onChange('', true, userGroupId);
      } else if (importedUserIds?.length) {
        for (const importedUserId of importedUserIds) {
          if (!isItemSelected(importedUserId)) {
            onChange(importedUserId, true);
          }
        }
      }
    },
    [isItemSelected, onChange],
  );

  const { onCloseModal, modalClosePrompt } = usePreserveModalForm({
    onClose,
    isDirty: canImport ? () => isImportInProgress : () => false,
  });

  const onGroupCreate = useCallback(
    (id: string) => {
      onChange(id, true);
    },
    [onChange],
  );

  const tableColumns = useMemo(
    () =>
      getTableColumns({
        t,
        isSelectAll,
        isPartiallySelected,
        onSelectAll: onSelectAllChange,
        inViewTab,
        isItemSelected,
        toggle: onChange,
        selectedCount,
        disabled,
      }),
    [
      t,
      isSelectAll,
      isPartiallySelected,
      onSelectAllChange,
      inViewTab,
      isItemSelected,
      onChange,
      selectedCount,
      disabled,
    ],
  );

  const renderAdaptedContents = () => {
    if (error) {
      return <ErrorState error={error} />;
    }

    if (data?.length) {
      return (
        <Rect>
          {({ width, height }) => (
            <Table
              className={cn(tableStyles, commonTableStyles)}
              columns={tableColumns}
              data={data}
              headerClassName="sticky top-0 !bg-bg-primary"
              headerHeight={inViewTab === Tabs.Learners ? 0 : 32}
              height={height}
              ignoreFunctionInColumnCompare={false}
              rowClassName="flex-1 w-full"
              rowHeight={inViewTab === Tabs.Learners ? 81 : 83}
              rowKey="id"
              width={width}
              onEndReached={fetchMore}
              onEndReachedThreshold={1000}
            />
          )}
        </Rect>
      );
    }

    if (isFetching) {
      return <Loader />;
    }

    return (
      <AdminEmptyState
        clearFilters={clearFilters}
        clearSearch={clearSearch}
        description={t(TRANSLATIONS[inViewTab].description, {
          searchString: search,
        })}
        filter={filter}
        search={search}
        showIllustrationIcon={false}
        title={t(TRANSLATIONS[inViewTab].title)}
      />
    );
  };

  return (
    <>
      <PreserveModalForm
        description={t('Admin:LearnerAndGroups.GoingBackAssignmentLearnerImportDescription')}
        variant="create"
        {...modalClosePrompt}>
        <ContentListModal.Main
          action={{
            discardText: secondaryButtonText ?? t('Common:Cancel'),
            onSubmit,
            text: primaryButtonText ?? t('Admin:LearnerAndGroups.AddLearners'),
            isSubmitting,
            disabled: isSubmitting || disabled,
            TertiaryAction:
              inViewTab == Tabs.Learners ? (
                !currentImportJob?.id && canImport ? (
                  <LinkButton
                    StartIcon={CloudUploadOutline}
                    className="group ms-16"
                    color="internal"
                    onClick={on}>
                    <Typography variant="label-01">
                      {t('Admin:BulkImport.ImportEmployees')}
                    </Typography>
                  </LinkButton>
                ) : undefined
              ) : (
                <LinkButton
                  StartIcon={Plus}
                  className="group ms-16"
                  color="internal"
                  onClick={() => toggleGroupCreationModal()}>
                  <Typography variant="label-01">
                    {t('Assignment:CreateGroup.FormTitle')}
                  </Typography>
                </LinkButton>
              ),
          }}
          fetchMore={fetchMore}
          header={{
            title: title ?? t('Admin:LearnerAndGroups.Title'),
            description: description ?? t('Admin:LearnerAndGroups.Description'),
          }}
          items={data}
          searchBar={{
            value: search,
            onChange: setSearch,
            selectedCount,
            placeholder: searchPlaceholder,
          }}
          showCloseBtn={!hideSecondaryAction}
          sortComponent={
            sortAndFilterItems ? (
              <SortAndFilter
                className="mt-20 mb-12 px-8"
                filter={filter}
                setFilter={setFilter}
                setSort={setSort}
                sort={sort}
                sortAndFilterItems={sortAndFilterItems}
              />
            ) : undefined
          }
          tabs={tabs}
          onClose={onCloseModal}>
          <Stack className="h-full w-full flex-1 px-24" direction="col">
            {inViewTab === Tabs.Learners && canImport ? (
              <BulkImport
                className="mb-28 !mt-8"
                currentImportJob={currentImportJob}
                failedSnackbarMessage={t('Admin:BulkImport.LearnersImportFailed')}
                fileInputRef={fileInputRef}
                jobPurpose={[JobPurposeEnum.AssignmentLearnerImport]}
                refetchJobs={refetchJobs}
                updateDataDialogProps={{ getDescription: getUpdateDataDialogDescription }}
                uppy={uppy}
                onClose={onCloseImport}
                onComplete={onJobComplete}
                onImportStatusUpdate={setImportJobStatus}
              />
            ) : null}
            <Stack className="flex-1">{renderAdaptedContents()}</Stack>
          </Stack>
        </ContentListModal.Main>
      </PreserveModalForm>
      {canImport && showLearnerImportDialog ? (
        <BulkImportDialog
          description={t('Admin:BulkImport.BulkImportAssignmentLearnersDescription')}
          jobPurpose={JobPurposeEnum.AssignmentLearnerImport}
          templateFileName={TEMPLATE_FILE_NAME}
          title={t('Admin:BulkImport.ImportEmployees')}
          onClose={off}
          onSubmit={showFileSelector}
        />
      ) : null}
      {openGroupCreationModal ? (
        <CreateGroupModal onClose={toggleGroupCreationModal} onGroupCreate={onGroupCreate} />
      ) : null}
    </>
  );
}

export default LearnerAndGroupDialog;
